import axios from 'axios';
import { User, Challenge, CommandResponse, ValidationResponse, ContainerResponse, UserProgress, OAuthProvider, OAuthResponse } from '../types';

// In development, use relative URLs which will be handled by the Vite dev server proxy
const isDevelopment = import.meta.env.DEV;
const API_BASE = isDevelopment ? '' : (import.meta.env.VITE_API_BASE_URL || 'https://api.opsquest.dev');

const api = axios.create({
  baseURL: API_BASE,
  timeout: 300000, // 5 minutes
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Add this function to call the refresh endpoint
async function refreshToken() {
  const refresh_token = localStorage.getItem('refresh_token');
  if (!refresh_token) throw new Error('No refresh token available');
  const response = await axios.post(`${API_BASE}/v1/auth/refresh`, { refresh_token });
  if (response.data?.access_token) {
    localStorage.setItem('token', response.data.access_token);
    return response.data.access_token;
  } else {
    throw new Error('Failed to refresh token');
  }
}

// Token refresh queue logic
let isRefreshing = false;
let failedQueue: any[] = [];

function processQueue(error: any, token: string | null = null) {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
}

// Handle auth errors and response errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        api.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
        processQueue(null, newToken);
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
        return api(originalRequest);
      } catch (err) {
        processQueue(err, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    } else if (error.response?.status === 403) {
      // Handle forbidden errors
      console.error('Access forbidden:', error.response?.data);
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    } else if (error.response?.status === 504) {
      // Handle gateway timeout
      throw new Error('The server took too long to respond. Please try again in a few minutes.');
    } else if (!error.response) {
      // Handle network errors
      console.error('Network error:', error.message);
      if (error.message.includes('timeout')) {
        throw new Error('Request timed out. Please check your connection and try again.');
      } else if (error.message.includes('Network Error')) {
        throw new Error('Unable to connect to the server. Please check your internet connection and try again.');
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  async login(username: string, password: string) {
    try {
      const response = await api.post('/v1/auth/login', { 
        username: username.trim(),
        password: password
      });
      
      if (response.data?.access_token) {
        localStorage.setItem('token', response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        return response.data;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Login error:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        throw new Error('Invalid username or password');
      } else if (error.response?.status === 422) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          throw new Error(detail[0].msg || 'Invalid input data');
        } else if (typeof detail === 'string') {
          throw new Error(detail);
        } else {
          throw new Error('Invalid input data');
        }
      } else if (error.response?.status === 429) {
        throw new Error('Too many login attempts. Please try again later.');
      } else if (error.message === 'Network Error') {
        throw new Error('Unable to connect to the server. Please check your internet connection.');
      } else {
        throw new Error('Login failed. Please try again.');
      }
    }
  },

  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const response = await api.post('/v1/auth/check-email', { email: email.toLowerCase() });
      return response.data.exists;
    } catch (error) {
      console.warn('Email check failed:', error);
      return false;
    }
  },

  async register(_username: string, _email: string, _password: string) {
    // Temporarily disable registration
    throw new Error('Registration is temporarily disabled while we perform maintenance. Please try again later.');
  },

  async getCurrentUser(): Promise<User> {
    const response = await api.get('/v1/user/me');
    const userData = response.data;
    const mappedUser = {
      ...userData,
      role: Boolean(userData.is_admin) ? 'admin' as const : 'user' as const
    };
    return mappedUser;
  },

  async loginWithOAuth(provider: OAuthProvider, code: string): Promise<OAuthResponse> {
    try {
      const response = await api.post(`/v1/auth/${provider}/callback`, { code });
      if (response.data?.access_token) {
        localStorage.setItem('token', response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem('refresh_token', response.data.refresh_token);
        }
        // Add expires_at if not present
        if (!response.data.expires_at && response.data.expires_in) {
          response.data.expires_at = new Date(Date.now() + response.data.expires_in * 1000).toISOString();
        }
        return response.data;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error(`${provider} login error:`, error.response?.data || error.message);
      if (error.response?.status === 401) {
        throw new Error(`Authentication with ${provider} failed`);
      } else if (error.response?.status === 422) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          throw new Error(detail[0].msg || 'Invalid input data');
        } else if (typeof detail === 'string') {
          throw new Error(detail);
        } else {
          throw new Error('Invalid input data');
        }
      } else if (error.response?.status === 429) {
        throw new Error('Too many login attempts. Please try again later.');
      } else if (error.message === 'Network Error') {
        throw new Error('Unable to connect to the server. Please check your internet connection.');
      } else {
        throw new Error(`${provider} login failed. Please try again.`);
      }
    }
  },

  getOAuthUrl(provider: OAuthProvider): string {
    const baseUrl = API_BASE;
    return `${baseUrl}/v1/auth/${provider}/login`;
  }
};

export const challengeAPI = {
  async getChallenges(): Promise<Challenge[]> {
    try {
      const response = await api.get('/v1/challenges');
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'Failed to fetch challenges. Please try again.';
      throw new Error(errorMessage);
    }
  },

  async getChallenge(id: number): Promise<Challenge> {
    try {
      const response = await api.get(`/v1/challenges/${id}`);
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'Failed to load challenge details. Please try again.';
      throw new Error(errorMessage);
    }
  },

  async startChallenge(challengeId: number): Promise<ContainerResponse> {
    try {
      const response = await api.post(`/v1/challenges/${challengeId}/start`, {}, {
        timeout: 300000, // 5 minutes
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'Failed to start challenge. Please try again.';
      throw new Error(errorMessage);
    }
  },

  async validateSolution(challengeId: number, containerId: string): Promise<ValidationResponse> {
    try {
      const response = await api.post(`/v1/challenges/${challengeId}/validate?container_id=${containerId}`);
      return response.data;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.detail ||
        error.message ||
        'Failed to validate solution. Please try again.';
      throw new Error(errorMessage);
    }
  }
};

export const containerAPI = {
  async getActiveContainers() {
    try {
      console.log('containerAPI.getActiveContainers called'); // DEBUG LOG
      console.log('API base URL:', API_BASE); // DEBUG LOG
      console.log('Making request to:', `${API_BASE}/v1/user/active-containers`); // DEBUG LOG
      const response = await api.get('/v1/user/active-containers');
      console.log('getActiveContainers response:', response.data); // DEBUG LOG
      return response.data;
    } catch (error: any) {
      console.error('Error fetching active containers:', error);
      console.error('Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status
      }); // DEBUG LOG
      return { containers: [], total: 0 };
    }
  },

  async executeCommand(containerId: string, command: string): Promise<CommandResponse> {
    try {
      const response = await api.post(`/v1/containers/${containerId}/execute`, { command });
      return response.data;
    } catch (error: any) {
      console.error('Command execution error:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to execute command. Please try again.';
      throw new Error(errorMessage);
    }
  },

  async stopContainer(containerId: string) {
    try {
      await api.post(`/v1/containers/${containerId}/stop`);
    } catch (error) {
      console.error('Error stopping container:', error);
      // Don't throw, just log the error
    }
  }
};

export const userAPI = {
  async getProgress(): Promise<UserProgress[]> {
    try {
      const response = await api.get('/v1/user/progress');
      return response.data;
    } catch (error) {
      console.error('Error fetching user progress:', error);
      // Return empty progress array as fallback
      return [];
    }
  }
};

export const adminAPI = {
  // User Management
  async getUsers(page: number = 1, limit: number = 10, search?: string) {
    const response = await api.get('/v1/admin/users', {
      params: { page, limit, search }
    });
    return response.data;
  },

  async updateUser(userId: number, data: any) {
    const response = await api.put(`/v1/admin/users/${userId}`, data);
    return response.data;
  },

  async deleteUser(userId: number) {
    const response = await api.delete(`/v1/admin/users/${userId}`);
    return response.data;
  },

  async getUserLoginHistory(userId: number, page: number = 1, limit: number = 10) {
    const response = await api.get(`/v1/admin/users/${userId}/login-history`, {
      params: { page, limit }
    });
    return response.data;
  },

  // Challenge Management
  async getChallenges(page = 1, limit = 10) {
    const response = await api.get('/v1/admin/challenges', {
      params: { page, limit }
    });
    return response.data;
  },

  async createChallenge(data: Partial<Challenge>) {
    const response = await api.post('/v1/admin/challenges', data);
    return response.data;
  },

  async updateChallenge(challengeId: number, data: Partial<Challenge>) {
    const response = await api.put(`/v1/admin/challenges/${challengeId}`, data);
    return response.data;
  },

  async deleteChallenge(challengeId: number) {
    const response = await api.delete(`/v1/admin/challenges/${challengeId}`);
    return response.data;
  },

  // System Settings
  async getSystemSettings() {
    const response = await api.get('/v1/admin/settings');
    return response.data;
  },

  async updateSystemSettings(data: any) {
    const response = await api.put('/v1/admin/settings', data);
    return response.data;
  },

  // Analytics
  async getAnalytics(timeframe: string = '24h') {
    try {
      // Initialize default values
      const defaultAnalytics = {
        users: { total: 0, active: 0, new_today: 0 },
        challenges: { total: 0, active: 0, completion_rate: 0 },
        system: { cpu_usage: 0, memory_usage: 0, active_containers: 0 },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Try to fetch each type of stats independently
      const [userStats, challengeStats, systemStats] = await Promise.allSettled([
        api.get('/v1/admin/stats/users', { params: { timeframe } }),
        api.get('/v1/admin/stats/challenges', { params: { timeframe } }),
        api.get('/v1/admin/stats/system', { params: { timeframe } })
      ]);

      // Combine successful responses
      const analytics = { ...defaultAnalytics };
      
      if (userStats.status === 'fulfilled') {
        console.log('User stats loaded successfully:', userStats.value.data);
        Object.assign(analytics, userStats.value.data);
      } else {
        console.warn('Failed to load user stats:', userStats.reason);
      }
      
      if (challengeStats.status === 'fulfilled') {
        console.log('Challenge stats loaded successfully:', challengeStats.value.data);
        Object.assign(analytics, challengeStats.value.data);
      } else {
        console.warn('Failed to load challenge stats:', challengeStats.reason);
      }
      
      if (systemStats.status === 'fulfilled') {
        console.log('System stats loaded successfully:', systemStats.value.data);
        Object.assign(analytics, systemStats.value.data);
      } else {
        console.warn('Failed to load system stats:', systemStats.reason);
      }

      // Log the final combined analytics
      console.log('Combined analytics data:', analytics);
      return analytics;
    } catch (error: any) {
      console.error('Error in getAnalytics:', error);
      // Return default values instead of throwing
      return {
        users: { total: 0, active: 0, new_today: 0 },
        challenges: { total: 0, active: 0, completion_rate: 0 },
        system: { cpu_usage: 0, memory_usage: 0, active_containers: 0 },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
    }
  },

  async getChallengeStats() {
    const response = await api.get('/v1/admin/stats/challenges');
    return response.data;
  },

  async getSystemMetrics() {
    const response = await api.get('/v1/admin/stats/system');
    return response.data;
  },

  // Audit Logs
  async getAuditLogs(page = 1, limit = 10, filters?: {
    startDate?: string;
    endDate?: string;
    userId?: number;
    action?: string;
    search?: string;
  }) {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (filters?.startDate) params.append('start_date', filters.startDate);
    if (filters?.endDate) params.append('end_date', filters.endDate);
    if (filters?.userId) params.append('user_id', filters.userId.toString());
    if (filters?.action) params.append('action', filters.action);
    if (filters?.search) params.append('search', filters.search);
    const response = await api.get(`/v1/admin/audit-logs?${params.toString()}`);
    return response.data;
  },

  async toggleMaintenanceMode() {
    const response = await api.post('/v1/admin/settings/maintenance/toggle');
    return response.data;
  }
};

export default api;
